<template>
  <div class="epc-chart-container" ref="chartContainer">
    <canvas
      ref="chartCanvas"
      role="img"
      :aria-label="ariaLabel"
      tabindex="0"
      @keydown="onKeydown"
    ></canvas>
  </div>
</template>

<script setup lang="ts">
/**
 * Elevation-panel-v2's Chart.js wrapper — presentational only. Owns the
 * canvas and Chart.js instance and nothing else: no map/marker knowledge, no
 * bottom-sheet knowledge, no stats header.
 *
 * The area fill is always coloured by steepness (`steepnessColorForPercent`,
 * the same sequential scale the old standalone ribbon used) via a custom
 * `beforeDatasetsDraw` plugin, since Chart.js's built-in `fill` only supports
 * a single flat/gradient-by-position colour, not per-segment colouring keyed
 * on external data. The line itself is coloured by surface group
 * (`surfaceGroupForValue`) using Chart.js's own per-segment `segment.borderColor`
 * styling when the caller has meaningful surface data (`useSurfaceLineColor`);
 * otherwise it falls back to the flat brand gold, since recordings and
 * sparsely-tagged routes have no surface data worth showing.
 *
 * Emits raw `{index}`-based hover/hoverEnd/click; the parent
 * (ElevationPanelV2.vue) resolves `index -> lngLat/distanceMeters` using the
 * same `data.lats`/`lons`/`distancesMeters` arrays this component reads for
 * its own axes, and drives the map marker itself.
 *
 * IMPORTANT: Chart.js's own `onHover` callback is internally gated behind
 * `inChartArea` (see `Chart.prototype._handleEvent` in chart.js's source) —
 * it is NEVER invoked for the mouseout/pointerleave event itself, because by
 * definition the pointer is outside the chart area at that point. Relying on
 * `event.type === 'mouseout'` inside `onHover` is dead code (this bit
 * ElevationProfile.vue this session). The fix, reused here, is a native
 * `mouseleave` listener attached directly to the container that clears state
 * itself, bypassing Chart.js's callback entirely.
 */
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  type ChartConfiguration,
} from 'chart.js';
import type { ElevationChartData } from '../../../utils/elevation/buildElevationChartData';
import { ELEVATION_LINE_COLOR } from './elevationColors';
import { steepnessColorForPercent } from '../../../utils/elevation/steepnessGradient';
import { surfaceGroupForValue } from '../../../utils/elevation/surfaceGroups';

// No Tooltip plugin: the dedicated ScrubReadout tile (top-anchored, never
// under the finger on mobile) is the single source of truth for the hovered
// point's stats — Chart.js's own tooltip would otherwise render a second,
// overlapping "Distance/Elevation" box right on top of it.
Chart.register(LineController, LineElement, PointElement, LinearScale);

const STEEPNESS_FILL_ALPHA = 0.65;

const HOVER_LINE_COLOR = 'rgba(255, 223, 70, 0.5)';
// Dims the out-of-selection area to de-emphasise it, not hide it — at 0.55
// this crushed the vivid steepness fill/surface line down to a near-black
// smear (fine against the old flat, barely-visible gold wash; not against a
// saturated per-segment palette), reading as broken/missing data.
const SELECTION_DIM_COLOR = 'rgba(0, 0, 0, 0.3)';
// A thin, subtle dark "casing" drawn under the elevation line so it stays
// legible against the steepness fill, whose greens/yellows/oranges otherwise
// wash out a similarly-toned surface colour (e.g. naturalSoft's olive) drawn
// right on top of them with no separation. Kept narrow/low-opacity so the
// surface colour itself still reads clearly — this is a separator, not a
// second line.
const LINE_HALO_COLOR = 'rgba(0, 0, 0, 0.35)';
const LINE_HALO_EXTRA_WIDTH = 1.5;
// Index of the visible, coloured line dataset — the halo is drawn underneath
// it as dataset 0 (Chart.js draws datasets in array order).
const LINE_DATASET_INDEX = 1;

const props = withDefaults(
  defineProps<{
    data: ElevationChartData;
    activeIndex?: number | null;
    selectionRange?: { startIndex: number; endIndex: number } | null;
    ariaLabel?: string;
    /** Colour the line by surface group instead of the flat brand gold. Pass
     *  `false` (recordings, or planned routes with sparse surface tagging) to
     *  keep the flat gold line. */
    useSurfaceLineColor?: boolean;
  }>(),
  {
    activeIndex: null,
    selectionRange: null,
    ariaLabel: 'Elevation chart',
    useSurfaceLineColor: false,
  }
);

const emit = defineEmits<{
  (e: 'hover', payload: { index: number }): void;
  (e: 'hoverEnd'): void;
  (e: 'pointClick', payload: { index: number }): void;
}>();

const chartCanvas = ref<HTMLCanvasElement | null>(null);
const chartContainer = ref<HTMLElement | null>(null);
const chartInstance = shallowRef<Chart<'line'> | null>(null);
let isHoveringChart = false;

function buildChartConfig(): ChartConfiguration<'line'> {
  const d = props.data;
  const lastX = d.points.length ? d.points[d.points.length - 1].x : undefined;

  const lineWidth = props.useSurfaceLineColor ? 4 : 2;

  const dataset = {
    label: 'Elevation',
    data: d.points.slice(),
    borderColor: ELEVATION_LINE_COLOR,
    borderWidth: lineWidth,
    pointRadius: 0,
    pointHoverRadius: 4,
    pointHoverBackgroundColor: ELEVATION_LINE_COLOR,
    tension: 0.2,
    // The area fill is painted by epcSteepnessFill instead (per-segment
    // colour keyed on gradient %, which Chart.js's own `fill` can't express).
    fill: false,
    ...(props.useSurfaceLineColor
      ? { segment: { borderColor: (ctx: any) => surfaceGroupForValue(d.surfaces[ctx.p0DataIndex]).color } }
      : {}),
  };

  const haloDataset = {
    label: 'Elevation halo',
    data: d.points.slice(),
    borderColor: LINE_HALO_COLOR,
    borderWidth: lineWidth + LINE_HALO_EXTRA_WIDTH,
    pointRadius: 0,
    pointHoverRadius: 0,
    tension: 0.2,
    fill: false,
  };

  return {
    type: 'line',
    data: {
      labels: d.labels.slice(),
      // Halo first so Chart.js draws it underneath the coloured line
      // (dataset draw order follows array order).
      datasets: [haloDataset, dataset],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      animation: false,
      onHover: (event: any, activeElements: any[]) => {
        if (!activeElements || activeElements.length === 0) return;
        isHoveringChart = true;
        emit('hover', { index: activeElements[0].index });
      },
      onClick: (_event: any, activeElements: any[]) => {
        if (!activeElements || activeElements.length === 0) return;
        emit('pointClick', { index: activeElements[0].index });
      },
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: lastX,
          display: true,
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 10 },
            maxTicksLimit: 8,
            callback: (v: number | string) => `${v} ${d.unitLabels.distance}`,
          },
        },
        y: {
          display: true,
          beginAtZero: false,
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
          // Chart.js's default step-size selection can still land on ~10
          // gridlines for a route with a few hundred metres of relief (e.g.
          // steps of 10). Capping the tick count keeps the axis to the same
          // handful of "nice" round labels the reference design uses.
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 10 },
            maxTicksLimit: 5,
            callback: (v: number | string) => `${v} ${d.unitLabels.elevation}`,
          },
        },
      },
    },
  };
}

const steepnessFillPlugin = {
  id: 'epcSteepnessFill',
  beforeDatasetsDraw: (chart: Chart) => {
    const meta = chart.getDatasetMeta(LINE_DATASET_INDEX);
    const points = meta.data as unknown as { x: number; y: number }[];
    if (!points || points.length < 2) return;
    const gradients = props.data.gradients;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.lineTo(points[points.length - 1].x, bottom);
    ctx.lineTo(points[0].x, bottom);
    ctx.closePath();
    ctx.clip();

    ctx.globalAlpha = STEEPNESS_FILL_ALPHA;
    for (let i = 0; i < points.length - 1; i++) {
      const startX = points[i].x;
      const endX = points[i + 1].x;
      ctx.fillStyle = steepnessColorForPercent(gradients[i] ?? 0);
      // Slight overlap (0.5px) so adjacent segments don't leave a hairline
      // gap from sub-pixel rounding.
      ctx.fillRect(startX, top, Math.max(1, endX - startX + 0.5), bottom - top);
    }
    ctx.restore();
  },
};

const verticalGuidePlugin = {
  id: 'epcVerticalGuide',
  afterDraw: (chart: Chart) => {
    const active = chart.getActiveElements();
    if (!active || !active.length) return;
    const x = active[0].element.x;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chart.chartArea.top);
    ctx.lineTo(x, chart.chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = HOVER_LINE_COLOR;
    ctx.stroke();
    ctx.restore();
  },
};

const selectionShadePlugin = {
  id: 'epcSelectionShade',
  afterDatasetsDraw: (chart: Chart) => {
    const range = props.selectionRange;
    const d = props.data;
    if (!range || !d.points.length) return;
    const xScale = chart.scales.x;
    const { top, bottom } = chart.chartArea;
    const startX = xScale.getPixelForValue(d.points[range.startIndex]?.x ?? 0);
    const endX = xScale.getPixelForValue(d.points[range.endIndex]?.x ?? 0);
    const ctx = chart.ctx;
    ctx.save();
    ctx.fillStyle = SELECTION_DIM_COLOR;
    if (startX > chart.chartArea.left) {
      ctx.fillRect(chart.chartArea.left, top, startX - chart.chartArea.left, bottom - top);
    }
    if (endX < chart.chartArea.right) {
      ctx.fillRect(endX, top, chart.chartArea.right - endX, bottom - top);
    }
    ctx.restore();
  },
};

function destroyChart(): void {
  if (chartInstance.value) {
    try {
      chartInstance.value.stop();
    } catch {
      // no-op
    }
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
}

function redrawChart(): void {
  if (!chartCanvas.value) return;
  if (!props.data.points.length) {
    destroyChart();
    return;
  }
  const ctx = chartCanvas.value.getContext('2d');
  if (!ctx) return;

  destroyChart();
  chartInstance.value = markRaw(
    new Chart(ctx, {
      ...buildChartConfig(),
      plugins: [steepnessFillPlugin, verticalGuidePlugin, selectionShadePlugin],
    })
  );
  applyActiveIndex(props.activeIndex ?? null);
}

function applyActiveIndex(index: number | null): void {
  const chart = chartInstance.value;
  if (!chart) return;
  chart.setActiveElements(index == null ? [] : [{ datasetIndex: LINE_DATASET_INDEX, index }]);
  chart.update('none');
}

function handleHoverEnd(): void {
  if (!isHoveringChart) return;
  isHoveringChart = false;
  emit('hoverEnd');
}

function onKeydown(event: KeyboardEvent): void {
  const n = props.data.points.length;
  if (!n) return;
  const current = props.activeIndex ?? -1;

  if (event.key === 'ArrowRight') {
    event.preventDefault();
    emit('hover', { index: Math.min(n - 1, current < 0 ? 0 : current + 1) });
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    emit('hover', { index: Math.max(0, current < 0 ? n - 1 : current - 1) });
  } else if (event.key === 'Home') {
    event.preventDefault();
    emit('hover', { index: 0 });
  } else if (event.key === 'End') {
    event.preventDefault();
    emit('hover', { index: n - 1 });
  } else if (event.key === 'Escape') {
    event.preventDefault();
    handleHoverEnd();
  }
}

watch(() => props.activeIndex, (index) => applyActiveIndex(index ?? null));
watch(
  () => props.selectionRange,
  () => chartInstance.value?.update('none'),
  { deep: true }
);
watch(() => props.data, () => nextTick(() => redrawChart()));
watch(() => props.useSurfaceLineColor, () => nextTick(() => redrawChart()));

onMounted(() => {
  nextTick(() => redrawChart());
  chartContainer.value?.addEventListener('mouseleave', handleHoverEnd);
});

onBeforeUnmount(() => {
  chartContainer.value?.removeEventListener('mouseleave', handleHoverEnd);
  destroyChart();
});

defineExpose({ redrawChart });
</script>

<style scoped>
.epc-chart-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.epc-chart-container canvas {
  /* Own the full touch gesture inside the chart (scrub) — the bottom sheet's
     content area allows pan-y for scrolling, but the chart itself must not
     let a vertical component of a drag be eaten by that as a page/sheet
     scroll instead of a scrub. */
  touch-action: none;
  outline: none;
}

.epc-chart-container canvas:focus-visible {
  outline: 2px solid #ffdf46;
  outline-offset: -2px;
}
</style>
